/* eslint no-console: 0 */
import ArrowBackIosRoundedIcon from '@material-ui/icons/ArrowBackIosRounded';
import ArrowForwardIosRoundedIcon from '@material-ui/icons/ArrowForwardIosRounded';
import moment from 'moment-timezone';
import React, { useCallback, useMemo, useState } from 'react';
import { Bar, BarChart, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import styled from 'styled-components';
import { useDateRangeForTimePeriod } from '../../../hooks/useDatesForTimePeriod.ts';
import { METERS_PER_MILE, OdometerHistoryDatapoint, useDeviceOdometerHistory } from '../../../hooks/useDeviceOdometerHistory.ts';
import { GraphTimePeriod, describeTimePeriod, granularityForPeriod, nextPeriod, tickFormatter, tooltipFormatter } from '../../../services/device-mileage-utils.ts';
import TileV2 from '../../bits/TileV2.tsx';
import Loading from '../Loading.jsx';
import { triggerHapticFeedback } from '../../../hapticFeedback.ts';

interface DeviceMileagePanelProps {
  device: any;
  disabled?: boolean;
}

const cellRenderer = (entry, ix) => <Cell key={`cell-${ix}`} fill={entry.isInFuture ? '#F2F4F5' : '#C1D2E3'} />;

const GraphHeader = styled.div`
  display: flex;
  flex-direction: row;
  min-width: 220px;
`;

const GraphHeaderText = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  flex-grow: 1;
`;

const GraphAction = styled.div`
  display: flex
`;

const GraphActionButton = styled.button`
  background-color: transparent;
  border: none;
  cursor: pointer;
  color: rgb(180, 180, 180);
  padding-top: 8px;
  
  // This media query will apply the hover effect only on devices that can hover (i.e., non-touch devices)
  @media (hover: hover) and (pointer: fine) {
    &:hover {
      color: rgb(140, 140, 140);
    }
  }
  &:disabled {
    color: rgb(220, 220, 220);
    pointer-events: none;
  }

  // Remove the blue/grey box that happens on mobile press
  -webkit-tap-highlight-color: transparent;

  // Remove the focus outline (gray box) sometimes shown for accessibility
  &:focus {
    outline: none;
  }
`;

const LargeText = styled.div`
  font-size: 1.4em;
  font-weight: 500;
`;

const SmallText = styled.div`
  line-height: 1;
`;

const GraphContainer = styled.div`
  /* So we can position that child LoadingContainer (below..) */
  position: relative;

  padding: 8px 14px 8px 8px;
  display: flex;
  flex-direction: column;
  font-size: 0.75em;
`;

const LoadingContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  margin: auto;

  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 99;
`;

const GraphContent = styled.div<{ visible?: boolean }>`
  visibility: ${props => props.visible ?? true ? 'visible' : 'hidden'};
  display: flex;
  flex-direction: column;
  align-items: center;
  user-select: none;
`;

type OdometerHistoryDatapointEnriched = OdometerHistoryDatapoint & {
  distanceMiles: number;
  time: Date;
  isInFuture: boolean;
}
/**
 * Component that lists devices belonging to the user.
 */
export const DeviceMileagePanel: React.FC<DeviceMileagePanelProps> = ({ device, disabled = false }) => {

  const [timezone] = useState(moment.tz.guess());

  const [period, setPeriod] = useState<GraphTimePeriod>('today');
  const periodLabel = useMemo(() => describeTimePeriod(period), [period]);
  const graphGranularity = useMemo(() => granularityForPeriod(period), [period]);
  const { startAt, endAt } = useDateRangeForTimePeriod(period);

  const { data: mileageHistoryResponse, isLoading } = useDeviceOdometerHistory({
    imei: device.imei,
    startAt,
    endAt,
    timezone,
    granularity: graphGranularity,
  });

  const odometerHistoryFormatted: OdometerHistoryDatapointEnriched[] = useMemo(() => {
    if (!mileageHistoryResponse?.data) {
      return [];
    }

    return mileageHistoryResponse.data.map((datapoint) => {
      const timeMoment = moment.tz(datapoint.key, timezone);

      return {
        ...datapoint,
        distanceMiles: datapoint.distanceMeters / METERS_PER_MILE,
        time: timeMoment.toDate(),
        isInFuture: timeMoment.isAfter(),
      };
    });
  }, [mileageHistoryResponse]);

  const totalDistanceMiles = useMemo(() => {
    if (!mileageHistoryResponse?.data) {
      return 0;
    }

    const sumMeters = mileageHistoryResponse?.data.reduce((acc, e) => acc + e.distanceMeters, 0);
    return sumMeters / METERS_PER_MILE;
  }, [mileageHistoryResponse]);

  const onGraphBack = useCallback(() => {
    triggerHapticFeedback();
    setPeriod(nextPeriod(period, -1));
  }, [period]);

  const onGraphForward = useCallback(() => {
    triggerHapticFeedback();
    setPeriod(nextPeriod(period, 1));
  }, [period]);

  return (
    <TileV2 disabled={disabled}>
      <GraphContainer>
        {isLoading && <LoadingContainer><Loading /></LoadingContainer>}
        <GraphContent>
          <GraphHeader>
            <GraphAction>
              <GraphActionButton onClick={onGraphBack} disabled={period === 'last_month'}>
                <ArrowBackIosRoundedIcon style={{ position: 'relative', left: '5px' }}/>
              </GraphActionButton>
            </GraphAction>
            <GraphHeaderText>
              <LargeText>{totalDistanceMiles.toFixed(1)}</LargeText>
              <SmallText>Miles {periodLabel}</SmallText>
            </GraphHeaderText>
            <GraphAction>
              <GraphActionButton onClick={onGraphForward} disabled={period === 'today'}>
                <ArrowForwardIosRoundedIcon />
              </GraphActionButton>
            </GraphAction>
          </GraphHeader>
          <ResponsiveContainer width="100%" height={120}>
            <BarChart data={odometerHistoryFormatted}>
              <Bar dataKey="distanceMiles" minPointSize={3}>
                { odometerHistoryFormatted.map(cellRenderer) }
              </Bar>
              <YAxis width={25} axisLine={false} tickLine={false} allowDecimals={false}/>
              <XAxis
                dataKey="time"
                hide={false}
                name = 'Time'
                axisLine={false}
                tickLine={false}
                allowDataOverflow={false}
                tickFormatter={(time: Date) => tickFormatter(time, period)}
                interval={['last_week', 'this_week'].includes(period) ? 0 : 3}
                height={16}
                />
              <Tooltip
                formatter={(value: number) => [(value).toFixed(1), 'Miles']}
                labelFormatter={(value: Date) => tooltipFormatter(value, period)}
              />
            </BarChart>
          </ResponsiveContainer>
        </GraphContent>
      </GraphContainer>
    </TileV2>
  );
};
